import { useCallback, useEffect, useMemo, useState } from 'react';

import { env } from '@kc-console/context';
import { useLocalStorage } from '@kc-console/core';

type ConsoleVersionBannerStorage = Record<
  string,
  {
    expires: number;
  }
>;

const STORAGE_KEY = 'kc-console::service::console-version-banner';
const STORAGE_VERSION = 'v1.0';
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * 대시보드는 서비스 내에서 비욘드 콘솔 사용 안내 배너를 제공하므로 본 배너를 제공하지 않는다.
 */
const CONSOLE_VERSION_BANNER_HIDDEN_SERVICE_KEYWORDS = ['dashboard'];

export const useConsoleVersionBannerStorage = (serviceKeyword?: string) => {
  const [hasMounted, setHasMounted] = useState(false);
  const [storage, setStorage] = useLocalStorage<ConsoleVersionBannerStorage>(
    STORAGE_KEY,
    undefined,
    STORAGE_VERSION,
  );

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const shouldShow = useMemo(() => {
    if (
      !hasMounted ||
      !serviceKeyword ||
      CONSOLE_VERSION_BANNER_HIDDEN_SERVICE_KEYWORDS.includes(serviceKeyword)
    ) {
      return false;
    }

    if (env.KC_SHOW_CONSOLE_VERSION_BANNER !== 'true') {
      return false;
    }

    const { expires } = storage?.[serviceKeyword] ?? {};

    if (!expires) {
      return true;
    }

    if (Date.now() > expires) {
      return true;
    }

    return false;
  }, [hasMounted, serviceKeyword, storage]);

  const setHideForAWeek = useCallback(() => {
    if (!serviceKeyword) {
      return;
    }

    setStorage(prev => ({
      ...(prev ?? {}),
      [serviceKeyword]: {
        expires: Date.now() + ONE_WEEK_MS,
      },
    }));
  }, [serviceKeyword, setStorage]);

  return { shouldShow, setHideForAWeek };
};
